import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // CRITICAL: if returning from Google OAuth, skip the /me check —
    // AuthCallback will exchange the session_id and establish the session.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Session lives in an httpOnly cookie — ask the backend who we are.
    api
      .me()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    const res = await api.login(email, password);
    setUser(res.user);
    // Remember role for tailored Login page copy on future visits
    try {
      if (res.user?.role) localStorage.setItem("jero_last_role", res.user.role);
    } catch (e) { /* ignore quota errors */ }
    return res.user;
  };

  const register = async (body) => {
    await api.register(body);
    return login(body.email, body.password);
  };

  const logout = async () => {
    try { await api.logout(); } catch (e) { /* cookie clear is best-effort */ }
    // Note: we intentionally KEEP `jero_last_role` so the Login page can
    // still show the tailored copy on next visit. User can clear it via
    // the "Not you? See both views" link.
    setUser(null);
    window.location.href = "/login";
  };

  const refreshMe = async () => {
    const u = await api.me();
    setUser(u);
    return u;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
